/**
 * Selector del rango de la página de Estadísticas: 7 días · 30 días · 90 días · Este mes ·
 * Mes pasado · Personalizado (dos `<input type="date">`). El rango vive en la URL: la página
 * decide qué hacer con lo elegido; acá sólo se valida el personalizado antes de aplicarlo.
 */

import { useEffect, useId, useState } from "react";
import type { ClaveRango } from "../../logic/estadisticas";
import { RANGOS, validarRango } from "../../logic/estadisticas";
import type { RangoFechas } from "../../types.estadisticas";

export function SelectorRango({ clave, rango, onElegir, onPersonalizado }: {
  clave: ClaveRango | "personalizado"; rango: RangoFechas;
  onElegir: (c: ClaveRango) => void; onPersonalizado: (r: RangoFechas) => void;
}) {
  const [abierto, setAbierto] = useState(clave === "personalizado");
  const [desde, setDesde] = useState(rango.desde);
  const [hasta, setHasta] = useState(rango.hasta);
  const [error, setError] = useState("");
  const idDesde = useId();
  const idHasta = useId();

  // Si el rango cambia desde afuera (otro botón, la URL), los inputs lo acompañan.
  useEffect(() => { setDesde(rango.desde); setHasta(rango.hasta); }, [rango.desde, rango.hasta]);

  const aplicar = () => {
    const e = validarRango(desde, hasta);
    setError(e || "");
    if (!e) onPersonalizado({ desde, hasta });
  };

  const boton = (activo: boolean) => `h-8 px-3 rounded-lg text-xs font-semibold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
    activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`;

  return (
    <div className="space-y-2">
      <div role="group" aria-label="Rango de fechas" className="inline-flex max-w-full items-center gap-1 rounded-xl bg-slate-100 p-1 overflow-x-auto">
        {RANGOS.map((r) => (
          <button key={r.clave} type="button" aria-pressed={clave === r.clave} className={boton(clave === r.clave)}
            onClick={() => { setAbierto(false); setError(""); onElegir(r.clave); }}>
            {r.label}
          </button>
        ))}
        <button type="button" aria-pressed={clave === "personalizado"} aria-expanded={abierto} className={boton(clave === "personalizado" || abierto)}
          onClick={() => setAbierto((v) => !v)}>
          Personalizado
        </button>
      </div>
      {abierto && (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor={idDesde} className="block text-xs font-medium text-slate-600">Desde</label>
            <input id={idDesde} type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              className="mt-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <div>
            <label htmlFor={idHasta} className="block text-xs font-medium text-slate-600">Hasta</label>
            <input id={idHasta} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              className="mt-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <button type="button" onClick={aplicar} className="h-9 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700">Aplicar</button>
          {error && <p className="w-full text-xs text-red-600" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}

export default SelectorRango;
